/**
 * VAT / consumption-tax calculations.
 *
 * Region-aware:
 *   - Botswana (BW): 14% (BURS standard rate, current)
 *   - South Africa (ZA): 15% (SARS)
 *   - Nigeria (NG): 7.5% (FIRS)
 *
 * The Botswana legacy 12% rate is preserved for back-dated entries pre
 * the BURS rate change. The original BW-only signatures still work
 * (rate defaults to BW 14%) so existing call sites and the test suite
 * don't change.
 */

import { getRegion, type RegionCode } from './region';

export const BW_VAT_RATE = 0.14;
export const BW_VAT_RATE_LEGACY = 0.12;

/** Look up the standard VAT rate for a region. */
export function vatRateFor(region: RegionCode | string): number {
  return getRegion(region).vat.rate;
}

/** VAT-exclusive amount + VAT = VAT-inclusive amount. */
export function addVat(
  net: number,
  rate: number = BW_VAT_RATE,
): {
  net: number;
  vat: number;
  gross: number;
} {
  const vat = round2(net * rate);
  return { net: round2(net), vat, gross: round2(net + vat) };
}

/** Strip VAT out of a VAT-inclusive total. */
export function stripVat(
  gross: number,
  rate: number = BW_VAT_RATE,
): {
  net: number;
  vat: number;
  gross: number;
} {
  const net = round2(gross / (1 + rate));
  const vat = round2(gross - net);
  return { net, vat, gross: round2(gross) };
}

/** Apply the rate that was in force on a given date for Botswana.
 *  Current standard rate is 14%; older legacy 12% reference kept for
 *  back-dated historical entries that pre-date BURS rate changes. */
export function rateForDate(_date: Date): number {
  return BW_VAT_RATE;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
